import React, { useEffect, useId, useRef } from 'react';

import { format, isValid, parse } from 'date-fns';

import { DatePicker } from '@/design-system/date-picker';
import { cn } from '@/design-system/lib/utils';

import { useFieldA11y } from './form';

/** The wire format of a calendar day: the API, the URL and the form state. */
const ISO_DAY = 'yyyy-MM-dd';

/** `YYYY-MM-DD` of a picked day. */
export const toIsoDay = (d: Date): string => format(d, ISO_DAY);

/** The calendar day of a `YYYY-MM-DD` string; undefined when blank or invalid. */
export const fromIsoDay = (value: string): Date | undefined => {
  if (!value) return undefined;
  const d = parse(value, ISO_DAY, new Date());
  return isValid(d) ? d : undefined;
};

interface DateFieldProps {
  /** `YYYY-MM-DD`; '' = no day picked. Picking the chosen day again clears it. */
  value: string;
  onChange: (value: string) => void;
  /** Id of the trigger button, so a `<label htmlFor>` reaches it. */
  id?: string;
  size?: 'sm' | 'md';
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /**
   * `aria-required` plus the kit Label's marker, for a field that has no
   * FormField label of its own (a table-line cell).
   */
  required?: boolean;
  className?: string;
  /** Accessible name when the field is not inside a FormField. */
  'aria-label'?: string;
  /** Overrides the enclosing FormField's error / hint line. */
  'aria-describedby'?: string;
  'data-testid'?: string;
}

/**
 * The kit DatePicker over a `YYYY-MM-DD` string: the WMS forms and filters
 * keep storing plain day strings, and no OS-native date input is rendered.
 * The kit trigger takes no id / aria props, so (like `usePickerA11y`) the
 * wrapper names it from the enclosing FormField label or `aria-label` and
 * points it at the field's error / hint line.
 * TODO(kit): replace with `id` / `aria-*` props on DatePicker (and the picked
 * day in the trigger's accessible name).
 */
export const DateField: React.FC<DateFieldProps> = ({
  value,
  onChange,
  id,
  size = 'md',
  placeholder = 'Pilih tanggal',
  disabled,
  invalid,
  required,
  className,
  'aria-label': ariaLabel,
  'aria-describedby': describedByProp,
  'data-testid': testId,
}) => {
  const field = useFieldA11y();
  const autoId = useId();
  const triggerId = id ?? autoId;
  const labelledBy = field?.labelId;
  const describedBy = describedByProp ?? field?.describedBy;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const trigger = ref.current?.querySelector<HTMLElement>(
      '[data-slot="date-picker-trigger"]'
    );
    if (!trigger) return;
    const set = (name: string, v: string | undefined) => {
      if (v) trigger.setAttribute(name, v);
      else trigger.removeAttribute(name);
    };
    set('id', triggerId);
    set('aria-label', ariaLabel);
    set('aria-labelledby', ariaLabel ? undefined : labelledBy);
    set('aria-describedby', describedBy);
    set('aria-invalid', invalid ? 'true' : undefined);
    set('aria-required', required ? 'true' : undefined);
  }, [triggerId, ariaLabel, labelledBy, describedBy, invalid, required]);

  return (
    <div
      ref={ref}
      className={cn('relative min-w-0', className)}
      data-testid={testId}
    >
      <DatePicker
        value={fromIsoDay(value)}
        onValueChange={(d) => onChange(d ? toIsoDay(d) : '')}
        placeholder={placeholder}
        format="dd MMM yyyy"
        size={size}
        disabled={disabled}
        triggerClassName={cn(invalid && 'border-error-base')}
      />
      {required && (
        <span
          aria-hidden
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-error-base"
        >
          *
        </span>
      )}
    </div>
  );
};
